import React, { FC, useEffect, useState } from 'react';
import Link from 'next/link';
import Image from 'next/legacy/image';
import { useRouter } from 'next/router';
import { useAuth0User } from '@hooks/useAuth0User';
import { useLoginHref } from '@hooks/useLoginHref';
import { AnalyticsEvents, captureAnalyticsEvent } from '@lib/analytics/events';
import FFHModal from '@components/modal';
import {
  BodyRegular,
  BodySmallRegular,
  H1
} from '@components/atoms/typography';
import Button from '@components/atoms/button';
import useWindowSize from '@hooks/useWindowSize';
import { getIsFreeMember } from '@lib/utils';
import { useMarketingCopy } from '@hooks/useMarketingCopy';

// FFH-2372: analytics placement for the fixed bottom paywall modal.
const BOTTOM_MODAL_PLACEMENT = 'page_paywall_bottom_modal';

type PaywallFixedBottomModalType = {
  show: boolean;
  handleClose: () => void;
  top?: string;
  title?: string;
  subtitle?: string;
  buttonText?: string;
  returnTo?: string;
  affiliate?: string;
};

const PaywallFixedBottomModal: FC<PaywallFixedBottomModalType> = ({
  show = false,
  handleClose,
  returnTo,
  affiliate = '',
  title,
  subtitle,
  buttonText
}) => {
  const copy = useMarketingCopy();
  const [showModal, setShowModal] = useState(show);
  const { user } = useAuth0User();
  const loginHref = useLoginHref(returnTo);
  const router = useRouter();

  // FFH-2372: the view is the modal actually opening, not the mount — the
  // component renders closed and the sessionStorage guard can keep it shut.
  useEffect(() => {
    if (!showModal) return;
    captureAnalyticsEvent(AnalyticsEvents.paywallViewed, {
      placement: BOTTOM_MODAL_PLACEMENT,
      feature: router.pathname
    });
  }, [showModal, router.pathname]);

  const checkSession = () => {
    const modalViewed = sessionStorage.getItem('modalViewed');

    if (modalViewed === null) {
      setShowModal(true);
    }
  };

  useEffect(() => {
    if (show) {
      checkSession();
    }
  }, [show]);

  const { width } = useWindowSize();

  const isFreeMember = getIsFreeMember(user);

  return (
    <FFHModal
      open={showModal}
      handleClose={() => {
        setShowModal(false);
        sessionStorage.setItem('modalViewed', 'true');
        handleClose();
      }}
      bottom
      iconColor={width > 758 ? 'text-neutral-50' : 'text-neutral-800'}
    >
      <div
        data-cy="paywall-modal"
        className="flex flex-col sm:flex-row justify-between"
      >
        <div className="flex flex-col p-4 md:py-12 md:px-24 max-w-[700px]">
          <H1 id="fixed-bottom-paywall-header-ai-team-rating">
            {title ?? copy.paywall.title}
          </H1>
          <BodyRegular
            className="mb-4"
            id="fixed-bottom-paywall-subheader-ai-team-rating"
          >
            {subtitle ?? copy.paywall.subtitle}
          </BodyRegular>
          <Button
            href={affiliate ? `/join?via=${affiliate}` : '/join'}
            isLink
            variant="primary"
            id="paywall-bottom-ai-team-rating"
            onClick={() => {
              // FFH-2372: a generic /join CTA points at no specific plan.
              captureAnalyticsEvent(AnalyticsEvents.upgradeCtaClicked, {
                placement: BOTTOM_MODAL_PLACEMENT,
                intended_tier: null
              });
            }}
          >
            {buttonText ?? copy.paywall.buttonText}
          </Button>

          {!isFreeMember && (
            <BodySmallRegular className="mt-3">
              Already a member?{' '}
              <Link
                prefetch={false}
                href={loginHref}
                className="text-primary-600 underline"
              >
                Log in
              </Link>
            </BodySmallRegular>
          )}
        </div>
        {width > 1000 && (
          <Image
            className="md:block"
            src={copy.paywall.image.url}
            width={660}
            height={330}
            objectFit="fill"
            alt={copy.paywall.image.alt}
          />
        )}
      </div>
    </FFHModal>
  );
};

export default PaywallFixedBottomModal;
